import React, { useState } from 'react';
import { Upload, AlertCircle, CheckCircle, Loader2, Table, FileSpreadsheet } from 'lucide-react';
import { Button } from '../ui/Button';
import { ProjectData, ProjectCategory } from '../../hooks/useProjects';
interface BulkImportProps {
  onImport: (projects: ProjectData[]) => Promise<void>;
}
export function BulkImport({
  onImport
}: BulkImportProps) {
  const [rawData, setRawData] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState<{
    success: number;
    errors: string[];
  } | null>(null);
  const parseProjects = (text: string): ProjectData[] => {
    const projects: ProjectData[] = [];
    const lines = text.trim().split('\n').filter(line => line.trim());
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      // Split by tab (Excel/Google Sheets format) or multiple spaces
      const columns = line.split(/\t+/).filter(col => col.trim());
      // If no tabs found, try splitting by multiple spaces or commas
      if (columns.length === 1) {
        const altColumns = line.split(/\s{2,}|,/).filter(col => col.trim());
        if (altColumns.length > 1) {
          columns.length = 0;
          columns.push(...altColumns);
        }
      }
      // Expected format: Nombre | Industria | Categoría | Descripción | Fecha | M² | Comuna
      if (columns.length >= 7) {
        const [name, industry, categoryRaw, description, dateStr, m2Str, comuna] = columns;
        // Parse category
        let category: ProjectCategory = 'Otros';
        const catLower = categoryRaw.toLowerCase();
        if (catLower.includes('sucursales bancarias') || catLower.includes('banco')) {
          category = 'Sucursales bancarias';
        } else if (catLower.includes('oficinas corporativas') || catLower.includes('oficina')) {
          category = 'Oficinas corporativas';
        } else if (catLower.includes('retail') || catLower.includes('tienda')) {
          category = 'Retail y tiendas';
        } else if (catLower.includes('educación') || catLower.includes('educacion') || catLower.includes('institucional')) {
          category = 'Educación / institucional';
        } else if (catLower.includes('industrial') || catLower.includes('especial')) {
          category = 'Industrial / especial';
        }
        // Parse date (format: may-2004 or 05-2004)
        const dateMatch = dateStr.match(/(\d{1,2}|\w{3,4})-(\d{4})/);
        let year = new Date().getFullYear();
        if (dateMatch) {
          year = parseInt(dateMatch[2]);
        }
        // Parse M²
        const m2 = parseInt(m2Str.replace(/[^\d]/g, '')) || 0;
        if (name && year && m2 > 0) {
          projects.push({
            name: name.trim(),
            category,
            description: description.trim(),
            year,
            m2,
            comuna: comuna.trim(),
            images: ['https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&q=80&w=1000'],
            featured: false
          });
        }
      }
    }
    return projects;
  };
  const handleImport = async () => {
    if (!rawData.trim()) {
      alert('Por favor pega los datos de los proyectos');
      return;
    }
    setIsProcessing(true);
    setResult(null);
    try {
      const projects = parseProjects(rawData);
      if (projects.length === 0) {
        setResult({
          success: 0,
          errors: ['No se pudieron parsear proyectos. Asegúrate de copiar directamente desde Excel/Google Sheets con las columnas correctas.']
        });
        return;
      }
      await onImport(projects);
      setResult({
        success: projects.length,
        errors: []
      });
      setRawData('');
    } catch (error: any) {
      setResult({
        success: 0,
        errors: [error.message || 'Error al importar']
      });
    } finally {
      setIsProcessing(false);
    }
  };
  return <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6" data-id="element-31">
      <div className="flex items-start gap-3 mb-6" data-id="element-32">
        <div className="p-2 bg-blue-50 rounded-lg" data-id="element-33">
          <FileSpreadsheet className="h-5 w-5 text-blue-600" data-id="element-34" />
        </div>
        <div className="flex-1" data-id="element-35">
          <h3 className="text-lg font-bold text-gray-900" data-id="element-36">
            Importación Masiva de Proyectos
          </h3>
          <p className="text-sm text-gray-500 mt-1" data-id="element-37">
            Copia y pega directamente desde Excel o Google Sheets. Selecciona
            las filas con datos y pégalas aquí.
          </p>
        </div>
      </div>

      {/* Formato de tabla esperado */}
      <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-38">
        <div className="flex items-start gap-2 mb-3" data-id="element-39">
          <Table className="h-4 w-4 text-blue-600 mt-0.5" data-id="element-40" />
          <p className="text-sm font-semibold text-blue-900" data-id="element-41">
            Formato de columnas (en este orden):
          </p>
        </div>

        <div className="overflow-x-auto" data-id="element-42">
          <table className="w-full text-xs border-collapse" data-id="element-43">
            <thead data-id="element-44">
              <tr className="bg-blue-100" data-id="element-45">
                <th className="border border-blue-300 px-3 py-2 text-left font-bold text-blue-900" data-id="element-46">
                  1. Nombre
                </th>
                <th className="border border-blue-300 px-3 py-2 text-left font-bold text-blue-900" data-id="element-47">
                  2. Industria
                </th>
                <th className="border border-blue-300 px-3 py-2 text-left font-bold text-blue-900" data-id="element-48">
                  3. Categoría
                </th>
                <th className="border border-blue-300 px-3 py-2 text-left font-bold text-blue-900" data-id="element-49">
                  4. Descripción
                </th>
                <th className="border border-blue-300 px-3 py-2 text-left font-bold text-blue-900" data-id="element-50">
                  5. Fecha
                </th>
                <th className="border border-blue-300 px-3 py-2 text-left font-bold text-blue-900" data-id="element-51">
                  6. M²
                </th>
                <th className="border border-blue-300 px-3 py-2 text-left font-bold text-blue-900" data-id="element-52">
                  7. Comuna
                </th>
              </tr>
            </thead>
            <tbody data-id="element-53">
              <tr className="bg-white" data-id="element-54">
                <td className="border border-blue-200 px-3 py-2 text-gray-700" data-id="element-55">
                  Banco Itaú
                </td>
                <td className="border border-blue-200 px-3 py-2 text-gray-600" data-id="element-56">
                  Bancos y servicios financieros
                </td>
                <td className="border border-blue-200 px-3 py-2 text-gray-600" data-id="element-57">
                  Sucursales bancarias
                </td>
                <td className="border border-blue-200 px-3 py-2 text-gray-600" data-id="element-58">
                  Habilitación Sucursal Viña del Mar 3
                </td>
                <td className="border border-blue-200 px-3 py-2 text-gray-600" data-id="element-59">
                  may-2004
                </td>
                <td className="border border-blue-200 px-3 py-2 text-gray-600" data-id="element-60">
                  454
                </td>
                <td className="border border-blue-200 px-3 py-2 text-gray-600" data-id="element-61">
                  Viña del Mar
                </td>
              </tr>
              <tr className="bg-blue-50" data-id="element-62">
                <td className="border border-blue-200 px-3 py-2 text-gray-700" data-id="element-63">
                  Scotiabank
                </td>
                <td className="border border-blue-200 px-3 py-2 text-gray-600" data-id="element-64">
                  Bancos y servicios financieros
                </td>
                <td className="border border-blue-200 px-3 py-2 text-gray-600" data-id="element-65">
                  Sucursales bancarias
                </td>
                <td className="border border-blue-200 px-3 py-2 text-gray-600" data-id="element-66">
                  Escala Auxiliar Exterior Alameda 969
                </td>
                <td className="border border-blue-200 px-3 py-2 text-gray-600" data-id="element-67">
                  jul-2013
                </td>
                <td className="border border-blue-200 px-3 py-2 text-gray-600" data-id="element-68">
                  902
                </td>
                <td className="border border-blue-200 px-3 py-2 text-gray-600" data-id="element-69">
                  Santiago
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="mt-4 bg-white rounded-lg p-3 border border-blue-200" data-id="element-70">
          <p className="text-xs font-bold text-gray-900 mb-2" data-id="element-71">
            📋 Cómo copiar desde Excel/Google Sheets:
          </p>
          <ol className="text-xs text-gray-700 space-y-1 list-decimal list-inside" data-id="element-72">
            <li data-id="element-73">Organiza tus datos en 7 columnas (en el orden de arriba)</li>
            <li data-id="element-74">
              Selecciona todas las filas con datos (sin incluir encabezados)
            </li>
            <li data-id="element-75">Copia (Ctrl+C o Cmd+C)</li>
            <li data-id="element-76">Pega aquí abajo (Ctrl+V o Cmd+V)</li>
            <li data-id="element-77">Click en "Importar Proyectos"</li>
          </ol>
        </div>

        <div className="mt-3 space-y-1" data-id="element-78">
          <p className="text-xs text-blue-800" data-id="element-79">
            <strong data-id="element-80">Categorías válidas:</strong> Sucursales bancarias, Oficinas
            corporativas, Retail y tiendas, Educación / institucional,
            Industrial / especial, Otros
          </p>
          <p className="text-xs text-blue-800" data-id="element-81">
            <strong data-id="element-82">Formato de fecha:</strong> mes-año (ej: may-2004, jul-2013)
            o número-año (ej: 05-2004)
          </p>
        </div>
      </div>

      <div className="space-y-4" data-id="element-83">
        <div data-id="element-84">
          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-85">
            Pega tus datos aquí (desde Excel/Google Sheets)
          </label>
          <textarea value={rawData} onChange={e => setRawData(e.target.value)} placeholder="Banco Itaú	Bancos y servicios financieros	Sucursales bancarias	Habilitación Sucursal	may-2004	454	Viña del Mar
Scotiabank	Bancos y servicios financieros	Sucursales bancarias	Escala Auxiliar Exterior	jul-2013	902	Santiago" className="w-full h-48 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm resize-none" data-id="element-86" />
          <p className="text-xs text-gray-500 mt-2" data-id="element-87">
            💡 <strong data-id="element-88">Tip:</strong> Selecciona las filas en Excel/Google Sheets
            y pégalas directamente aquí. Las columnas se detectarán
            automáticamente.
          </p>
        </div>

        {result && <div className={`p-4 rounded-lg ${result.errors.length > 0 ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`} data-id="element-89">
            <div className="flex items-start gap-3" data-id="element-90">
              {result.errors.length > 0 ? <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" data-id="element-91" /> : <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" data-id="element-92" />}
              <div className="flex-1" data-id="element-93">
                {result.errors.length > 0 ? <>
                    <p className="text-sm font-semibold text-red-900 mb-2" data-id="element-94">
                      Error en la importación
                    </p>
                    <ul className="text-sm text-red-800 list-disc list-inside" data-id="element-95">
                      {result.errors.map((error, i) => <li key={i} data-id="element-96">{error}</li>)}
                    </ul>
                  </> : <p className="text-sm font-semibold text-green-900" data-id="element-97">
                    ✅ {result.success} proyectos importados exitosamente a
                    Firebase
                  </p>}
              </div>
            </div>
          </div>}

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200" data-id="element-98">
          <Button variant="secondary" onClick={() => {
          setRawData('');
          setResult(null);
        }} disabled={isProcessing} data-id="element-99">
            Limpiar
          </Button>
          <Button onClick={handleImport} disabled={isProcessing || !rawData.trim()} leftIcon={isProcessing ? <Loader2 className="h-4 w-4 animate-spin" data-id="element-101" /> : <Upload className="h-4 w-4" data-id="element-102" />} data-id="element-100">
            {isProcessing ? 'Importando...' : 'Importar Proyectos'}
          </Button>
        </div>
      </div>
    </div>;
}